import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  ORDER_STATUS_LABEL,
  PAYMENT_FORM_LABEL,
  RECEIVABLE_TYPE_LABEL,
  billingApi,
  maskOrderMobile,
  type OrderListItem,
  type OrderListQuery,
  type OrderSource,
  type OrderStatus,
  type ReceivableType,
} from "../../api/billing.api";
import { orgCampusApi, type CampusItem } from "../../api/campuses.api";
import { courseSkuApi, type CourseSkuItem } from "../../api/course-skus.api";
import { productApi, type ProductItem } from "../../api/products.api";
import { EllipsisLink } from "../../components/EllipsisCell";
import { useHasPermission } from "../../auth/HasPermission";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { OrderCreateModal } from "./OrderCreateModal";
import { OrderDetailDrawer } from "./OrderDetailDrawer";
import { OrderStatusTag } from "./OrderStatusTag";
import { calendarOverdueDays } from "../../utils/datetime";

const { Title, Text } = Typography;

function formatTime(value: string | null): string {
  return value ? new Date(value).toLocaleString("zh-CN") : "-";
}

function shortId(value: string | null): string {
  return value ? `${value.slice(0, 8)}…` : "-";
}

/** ADM-09 订单与收款列表。 */
export function OrderListPage() {
  const [data, setData] = useState<OrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<OrderListQuery>({});
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [skus, setSkus] = useState<CourseSkuItem[]>([]);
  const [creating, setCreating] = useState(false);
  const canCreate = useHasPermission("ADM-09", "CREATE");
  const canExport = useHasPermission("ADM-09", "EXPORT");
  const [exporting, setExporting] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      orgCampusApi.listCampuses({ page: 1, pageSize: 100 }),
      productApi.listProducts({ page: 1, pageSize: 100 }),
      courseSkuApi.listCourseSkus({ page: 1, pageSize: 100 }),
    ])
      .then(([campusResult, productResult, skuResult]) => {
        setCampuses(campusResult.items);
        setProducts(productResult.items);
        setSkus(skuResult.items);
      })
      .catch(() => undefined);
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await billingApi.listOrders({
        ...filters,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "订单列表加载失败");
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const currentReceivable = useMemo(
    () => data.reduce((sum, item) => sum + item.receivableAmount, 0),
    [data],
  );
  const currentPaid = useMemo(
    () => data.reduce((sum, item) => sum + item.netReceivedAmount, 0),
    [data],
  );

  /** 按当前筛选条件导出全部匹配订单（服务端 CSV 流，手机号脱敏）。 */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await billingApi.exportOrders(filters);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "订单导出失败");
    } finally {
      setExporting(false);
    }
  }, [filters]);

  const columns: ColumnsType<OrderListItem> = [
    {
      title: "订单号",
      dataIndex: "orderNo",
      fixed: "left",
      width: 160,
      ellipsis: true,
      render: (value: string, record) => (
        <EllipsisLink
          value={value}
          onClick={() => setActiveOrderId(record.orderId)}
        />
      ),
    },
    { title: "学生", dataIndex: "studentName", width: 90 },
    {
      title: "监护人",
      dataIndex: "guardianMobile",
      width: 120,
      render: maskOrderMobile,
    },
    {
      title: "商品包",
      dataIndex: ["productSnapshot", "name"],
      width: 170,
      ellipsis: true,
    },
    { title: "校区", dataIndex: "campusName", width: 110 },
    {
      title: "原价",
      dataIndex: "originalAmount",
      width: 100,
      render: (value: number) => <MoneyDisplay amountInCents={value} />,
    },
    {
      title: "减免",
      dataIndex: "discountAmount",
      width: 100,
      render: (value: number) => <MoneyDisplay amountInCents={value} />,
    },
    {
      title: "应收",
      dataIndex: "receivableAmount",
      width: 100,
      render: (value: number) => <MoneyDisplay amountInCents={value} />,
    },
    {
      // 实收口径五处同源（order-manual-refund-admin-ui:SC-07）：Σ活跃节点已收 − 累计退款
      title: "实收",
      dataIndex: "netReceivedAmount",
      width: 100,
      render: (value: number, record) => (
        <span>
          <MoneyDisplay amountInCents={value} />
          {record.refundedAmount > 0 && (
            <Typography.Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
              已退
            </Typography.Text>
          )}
        </span>
      ),
    },
    {
      title: "款项形态",
      dataIndex: "paymentForm",
      width: 90,
      render: (value: OrderListItem["paymentForm"]) =>
        PAYMENT_FORM_LABEL[value],
    },
    {
      title: "当前节点",
      dataIndex: ["currentReceivablePlan", "type"],
      width: 90,
      render: (value?: ReceivableType) =>
        value ? RECEIVABLE_TYPE_LABEL[value] : "-",
    },
    {
      title: "节点到期",
      dataIndex: ["currentReceivablePlan", "dueAt"],
      width: 150,
      render: (_, record) => {
        const plan = record.currentReceivablePlan;
        if (!plan?.dueAt) return "-";
        return (
          <Space direction="vertical" size={0}>
            <span>{formatTime(plan.dueAt)}</span>
            {plan.overdue && plan.status === "PENDING" && (
              <Text type="danger">
                已逾期 {calendarOverdueDays(plan.dueAt)} 天
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "订单状态",
      dataIndex: "status",
      width: 90,
      render: (value: OrderStatus) => <OrderStatusTag status={value} />,
    },
    {
      title: "来源",
      dataIndex: "source",
      width: 105,
      render: (value: OrderSource) =>
        value === "PARENT_SELF" ? "家长自助" : "员工 / 管理端",
    },
    {
      title: "来源提交组",
      dataIndex: "sourceSubmissionId",
      width: 120,
      render: (value: string | null) =>
        value ? (
          <Tooltip title={value}>{shortId(value)}</Tooltip>
        ) : (
          <Text type="secondary">直建 / 复购</Text>
        ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      width: 160,
      render: (value: string) => formatTime(value),
    },
  ];

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>订单与收款</Title>
          <p className="page-header-desc">
            订单、应收节点与报名覆盖的全链路事实查询。
          </p>
        </div>
        <Space>
          {canExport && (
            <Button
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={() => void handleExport()}
            >
              导出
            </Button>
          )}
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreating(true)}
            >
              创建
            </Button>
          )}
        </Space>
      </div>
      <div className="page-filter-card">
        <Space wrap size={12}>
          <Input.Search
            allowClear
            placeholder="订单号 / 学生 / 手机号"
            style={{ width: 240 }}
            value={filters.keyword}
            onChange={(event) => {
              const keyword = event.target.value;
              setFilters((prev) => ({
                ...prev,
                keyword: keyword || undefined,
                orderNo: undefined,
                guardianMobile: undefined,
              }));
            }}
          />
          <Select
            allowClear
            placeholder="校区"
            style={{ width: 140 }}
            value={filters.campusId}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, campusId: value }))
            }
            options={campuses.map((campus) => ({
              value: campus.id,
              label: campus.name,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="商品包"
            style={{ width: 170 }}
            value={filters.productId}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, productId: value }))
            }
            options={products.map((product) => ({
              value: product.id,
              label: product.name,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="包含 SKU"
            style={{ width: 150 }}
            value={filters.skuId}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, skuId: value }))
            }
            options={skus.map((sku) => ({ value: sku.id, label: sku.name }))}
          />
          <Select
            allowClear
            placeholder="订单状态"
            style={{ width: 120 }}
            value={filters.status}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, status: value }))
            }
            options={Object.entries(ORDER_STATUS_LABEL).map(
              ([value, label]) => ({ value, label }),
            )}
          />
          <Select
            allowClear
            placeholder="节点类型"
            style={{ width: 110 }}
            value={filters.receivableType}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, receivableType: value }))
            }
            options={Object.entries(RECEIVABLE_TYPE_LABEL).map(
              ([value, label]) => ({ value, label }),
            )}
          />
          <Select
            allowClear
            placeholder="到期状态"
            style={{ width: 110 }}
            value={filters.dueStatus}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, dueStatus: value }))
            }
            options={[
              { value: "PENDING", label: "待收款" },
              { value: "OVERDUE", label: "已逾期" },
              { value: "SETTLED", label: "已结清" },
            ]}
          />
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </div>
      <div className="page-content-card">
        <Space size={24} style={{ marginBottom: 12 }}>
          <span>
            当前页应收：
            <b>
              <MoneyDisplay amountInCents={currentReceivable} />
            </b>
          </span>
          <span>
            当前页实收：
            <b>
              <MoneyDisplay amountInCents={currentPaid} />
            </b>
          </span>
        </Space>
        <Table
          rowKey="orderId"
          loading={loading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 1760 }}
          data-testid="admin-order-table"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </div>
      <OrderCreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setPage(1);
          void fetchList();
        }}
      />
      {activeOrderId && (
        <OrderDetailDrawer
          orderId={activeOrderId}
          onClose={() => setActiveOrderId(null)}
          onOrderChanged={() => void fetchList()}
        />
      )}
    </div>
  );
}
